const COLLECTION_SLIDER_COMPONENT = 'collection-slider';

if (!customElements.get(COLLECTION_SLIDER_COMPONENT)) {
  class CollectionSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.flickityInstance = null;
      this.filterButtons = null;
      this.productCards = null;
      this.isDragging = false;
    }

    connectedCallback() {
      this.initializeSlider();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initializeSlider() {
      this.initializeElements();
      this.setupFlickity();
      this.bindEvents();
      this.setHeight();
    }

    setHeight() {
      this.querySelector('.collection-slider__slider').style.minHeight = `${this.querySelector('.collection-slider__product-card').offsetHeight + 20}px`;
    }

    initializeElements() {
      this.slider = this.querySelector('.js-collection-slider');
      this.loader = this.querySelector('.js-collection-slider-loader');
      this.filterButtons = this.querySelectorAll('.js-collection-slider__tab-button');
      this.productCards = this.slider?.querySelectorAll('.js-collection-slider-product-card');
      this.nextButtons = this.querySelectorAll('.js-arrow-next');
      this.prevButtons = this.querySelectorAll('.js-arrow-prev');
      this.progressBar = this.querySelector('.js-progress-bar');
      this.progressFill = this.querySelector('.js-progress-fill');
    }

    setupFlickity() {
      if (!this.slider) return;

      const isMobile = window.innerWidth <= 768;

      this.flickityInstance = new Flickity(this.slider, {
        contain: true,
        groupCells: isMobile ? false : true,
        pageDots: false,
        cellAlign: 'left',
        cellSelector: '.js-collection-slider-product-card.js-active-card',
        on: {
          ready: () => {
            this.hideLoader();
            // Add a small delay to ensure Flickity is fully initialized
            setTimeout(() => {
              this.updateProgressBar();
            }, 100);
          },
          scroll: (progress) => {
            this.updateProgressBar();
          },
          settle: () => {
            this.updateProgressBar();
          },
          dragStart: () => {
            this.isDragging = true;
          },
          dragEnd: () => {
            this.isDragging = false;
            this.updateProgressBar();
          }
        }
      });
    }

    bindEvents() {
      this.nextButtons.forEach(button => {
        button.addEventListener('click', () => this.flickityInstance?.next());
      });
      this.prevButtons.forEach(button => {
        button.addEventListener('click', () => this.flickityInstance?.previous());
      });

      this.filterButtons.forEach(button => {
        button.addEventListener('change', (event) => this.handleFilterChange(event));
      });
    }

    handleFilterChange(event) {
      if (event.target.type !== 'radio') return;

      const filterInput = event.target.closest('.js-collection-slider__tab-button');
      const filterValue = filterInput?.value;

      if (!filterValue) return;
      this.showLoader();
      this.filterProducts(filterValue);
      this.rebuildSlider();
    }

    filterProducts(filterValue) {
      if (!this.productCards) return;

      if (filterValue === 'all') {
        this.showAllProducts();
      } else {
        this.showFilteredProducts(filterValue);
      }
    }

    showLoader() {
      if (this.loader) {
        this.loader.classList.add('is-active');
      }
    }

    hideLoader() {
      if (this.loader) {
        setTimeout(() => {
          this.loader.classList.remove('is-active');
        }, 300);
      }
    }

    showAllProducts() {
      this.productCards.forEach(card => {
        card.classList.add('js-active-card');
      });
    }

    showFilteredProducts(filterValue) {
      this.productCards.forEach(card => {
        card.classList.remove('js-active-card');
      });

      const filteredCards = this.querySelectorAll(`.js-collection-slider-product-card[data-filter="${filterValue}"]`);
      filteredCards.forEach(card => {
        card.classList.add('js-active-card');
      });
    }

    rebuildSlider() {
      if (!this.flickityInstance) return;

      this.flickityInstance.destroy();
      this.setupFlickity();

      setTimeout(() => {
        this.updateProgressBar();
      }, 100);
    }

    updateProgressBar() {
      if (!this.progressFill || !this.flickityInstance) return;

      const totalSlidesWidth = this.flickityInstance.slidesWidth;
      const visibleWidth = this.flickityInstance.size.innerWidth;

      if (totalSlidesWidth <= visibleWidth) {
        this.progressFill.style.width = '100%';
        return;
      }

      const maxScroll = totalSlidesWidth - visibleWidth;
      const currentScroll = Math.abs(this.flickityInstance.x);
      const scrollRatio = currentScroll / maxScroll;
      const viewportRatio = visibleWidth / totalSlidesWidth;
      let progressPercentage = (viewportRatio + scrollRatio * (1 - viewportRatio)) * 100;

      if (currentScroll < maxScroll - 5) {
        progressPercentage = Math.min(progressPercentage, 99);
      }

      const finalPercentage = Math.max(0, Math.min(100, progressPercentage));

      this.progressFill.style.width = `${finalPercentage}%`;
    }

    cleanup() {
      if (this.flickityInstance) {
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }
    }
  }

  customElements.define(COLLECTION_SLIDER_COMPONENT, CollectionSlider);
}